import { useId, useState, type ReactNode } from 'react';
import { Table2, LineChart as LineChartIcon } from 'lucide-react';
import { cn } from '@/components/ui/cn';
import { IconButton } from '@/components/ui/Button';

export interface Series {
  readonly key: string;
  readonly label: string;
  /** 1-based slot in the validated categorical palette. Never above 3. */
  readonly slot: 1 | 2 | 3;
}

export function seriesColour(slot: 1 | 2 | 3): string {
  return `var(--series-${slot})`;
}

/**
 * The shared chart shell: title, legend, and a table view.
 *
 * The table view is not a nicety. One slot in the palette sits just under 3:1
 * against the light surface, and the accessible relief for that is a visible
 * alternative reading of the same numbers — so every chart here ships one, and
 * it doubles as the answer to "what was that value exactly?".
 */
export function ChartFrame({
  title,
  description,
  series,
  table,
  children,
  className,
  actions,
}: {
  title: string;
  description?: string;
  /** Omit or pass one entry for a single-series chart: no legend box is drawn. */
  series?: readonly Series[];
  table: ReactNode;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const titleId = useId();

  const showLegend = (series?.length ?? 0) >= 2;

  return (
    <figure className={cn('m-0', className)}>
      <figcaption className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={titleId} className="text-sm font-semibold text-text">
            {title}
          </h3>
          {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <IconButton
            label={view === 'chart' ? 'Show the data as a table' : 'Show the chart'}
            onClick={() => setView((current) => (current === 'chart' ? 'table' : 'chart'))}
          >
            {view === 'chart' ? (
              <Table2 aria-hidden className="size-4" />
            ) : (
              <LineChartIcon aria-hidden className="size-4" />
            )}
          </IconButton>
        </div>
      </figcaption>

      {showLegend && view === 'chart' && (
        <ul className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {series!.map((entry) => (
            <li key={entry.key} className="inline-flex items-center gap-1.5 text-xs text-text-muted">
              <span
                aria-hidden
                className="inline-block h-0.5 w-3.5 rounded-full"
                style={{ backgroundColor: seriesColour(entry.slot) }}
              />
              {entry.label}
            </li>
          ))}
        </ul>
      )}

      {view === 'chart' ? <div aria-labelledby={titleId}>{children}</div> : table}
    </figure>
  );
}

/** The table rendering behind every chart's table view. */
export function ChartTable({
  columns,
  rows,
}: {
  columns: readonly string[];
  rows: readonly (readonly (string | number)[])[];
}) {
  return (
    <div className="scrollbar-thin max-h-72 overflow-auto rounded-md border border-border">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column}
                scope="col"
                className={cn(
                  'border-b border-border bg-surface-sunken px-2.5 py-1.5 text-[11px] font-semibold',
                  'tracking-wide text-text-subtle uppercase whitespace-nowrap',
                  index === 0 ? 'text-left' : 'text-right',
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    'border-b border-border px-2.5 py-1.5 text-text',
                    cellIndex === 0 ? 'text-left text-text-muted' : 'text-right tabular',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChartEmpty({ message = 'No data for this period' }: { message?: string }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border text-xs text-text-subtle">
      {message}
    </div>
  );
}
